import { Button, Icon } from "shared";
import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";
import { Divider } from "@yandex-int/hr-components/Divider";
import cn from "classnames";
import styles from "./modal-content.module.scss";

export interface IModalContentHeaderController {
    setTitle: React.Dispatch<React.SetStateAction<string | React.ReactElement | undefined>>;
    setSubtitle: React.Dispatch<React.SetStateAction<string | React.ReactElement | undefined>>;
}

const ModalContentHeaderContext = createContext<IModalContentHeaderController | null>(null);

/** Только внутри `ModalContent`: обновление заголовка/подзаголовка после монтирования. */
export function useModalContentHeader(): IModalContentHeaderController | null {
    return useContext(ModalContentHeaderContext);
}

interface Props extends PropsWithChildren {
    close: () => void;
    showButtonGroup?: boolean;
    onCancel?: () => void;
    onConfirm?: () => void;
    cancelText?: string;
    confirmText?: string;
    title?: string | React.ReactElement;
    subtitle?: string | React.ReactElement;
    className?: string;
    headerClassName?: string;
    disableConfirm?: boolean
    withHeader?: boolean
}

export const ModalContent: React.FC<Props> = ({
                                                  close,
                                                  children,
                                                  showButtonGroup = true,
                                                  cancelText,
                                                  onCancel,
                                                  confirmText,
                                                  onConfirm,
                                                  disableConfirm,
                                                  headerClassName,
                                                  title,
                                                  subtitle,
                                                  withHeader = true,
                                                  ...props
                                              }) => {
    const [resolvedTitle, setResolvedTitle] = useState(title);
    const [resolvedSubtitle, setResolvedSubtitle] = useState(subtitle);

    useEffect(() => {
        setResolvedTitle(title);
    }, [title]);

    useEffect(() => {
        setResolvedSubtitle(subtitle);
    }, [subtitle]);

    const headerController = useMemo<IModalContentHeaderController>(
        () => ({
            setTitle: setResolvedTitle,
            setSubtitle: setResolvedSubtitle,
        }),
        []
    );

    const hasHeaderText = Boolean(resolvedTitle || resolvedSubtitle);

    return (
        <ModalContentHeaderContext.Provider value={headerController}>
        <div className={cn(styles.container, props.className)}>
            {withHeader && (
                <>
                    <div className={cn(styles.headerContainer, hasHeaderText ? styles.withTitle : "", headerClassName ? headerClassName : "")}>
                        <div>
                            {resolvedTitle && <div className={styles.modalHeader}>{resolvedTitle}</div>}
                            {resolvedSubtitle && <div className={styles.modalSubtitle}>{resolvedSubtitle}</div>}
                        </div>
                        <Button view="ghost" onClick={close}>
                            <Icon glyph="close"/>
                        </Button>
                    </div>
                    <Divider/>
                </>
            )}
            <div className={cn(styles.content)}>
                {children}
            </div>
            {showButtonGroup && children && <Divider/>}
            {showButtonGroup && <div className={styles.buttonGroup}>
                {onConfirm && (
                    <Button
                        disabled={disableConfirm}
                        view="legal"
                        size="m"
                        onClick={() => {
                            onConfirm && onConfirm();
                            close();
                        }}>
                        {confirmText}
                    </Button>
                )}
                {onCancel && (
                    <Button
                        view="secondary"
                        size="m"
                        onClick={() => {
                            onCancel && onCancel();
                            close();
                        }}>
                        {cancelText}
                    </Button>
                )}

            </div>}
        </div>
        </ModalContentHeaderContext.Provider>
    )
}
